/**
 * oddit-header — Interactive Header Behavior
 * Handles: mobile menu toggle, desktop mega menu dropdowns, search drawer
 * Cart opens via the legacy hybrid cart (data-ns-hybrid-cart-open).
 */
(function () {
  'use strict';

  function initHeader(container) {
    var header = container || document.querySelector('.oddit-header');
    if (!header) return;

    /* ---------- Mobile Menu ---------- */
    var menuToggle = header.querySelector('.oddit-header__menu-toggle');
    var mobileMenu = header.querySelector('.oddit-header__mobile-menu');
    var mobileOverlay = header.querySelector('.oddit-header__mobile-menu-overlay');

    function openMobileMenu() {
      if (!mobileMenu) return;
      /* If the search drawer is currently open, dismiss it first — the user
         is switching contexts to the mobile nav, no need to keep search up. */
      if (document.documentElement.classList.contains('is-search-active')) {
        var headerSearchToggleEl = header.querySelector('.js-search-toggle');
        if (headerSearchToggleEl) headerSearchToggleEl.click();
      }
      mobileMenu.setAttribute('aria-hidden', 'false');
      if (menuToggle) {
        menuToggle.setAttribute('aria-expanded', 'true');
        menuToggle.setAttribute('aria-label', 'Close menu');
      }
      document.body.style.overflow = 'hidden';
    }

    function closeMobileMenu() {
      if (!mobileMenu) return;
      mobileMenu.setAttribute('aria-hidden', 'true');
      if (menuToggle) {
        menuToggle.setAttribute('aria-expanded', 'false');
        menuToggle.setAttribute('aria-label', 'Open menu');
      }
      document.body.style.overflow = '';
      /* If the global search drawer is layered on top of the mobile nav,
         dismiss it together — closing the nav underneath shouldn't leave
         a stranded search overlay. */
      if (document.documentElement.classList.contains('is-search-active')) {
        var headerSearchToggleEl = header.querySelector('.js-search-toggle');
        if (headerSearchToggleEl) headerSearchToggleEl.click();
      }
    }

    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', function () {
        var isOpen = mobileMenu.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          closeMobileMenu();
        } else {
          openMobileMenu();
        }
      });
    }

    if (mobileOverlay) {
      mobileOverlay.addEventListener('click', closeMobileMenu);
    }

    /* ---------- Mobile Search Trigger ----------
       The "search input" inside the mobile drawer is actually a button that
       opens the global search drawer (same one the header search icon opens).
       We forward the tap to the header's search toggle; the universal
       "auto-focus on open" handler below then drops the cursor in the input. */
    var mobileSearchTrigger = header.querySelector('.oddit-header__mobile-search-trigger');
    var headerSearchToggle = header.querySelector('.js-search-toggle');

    function getSearchInput() {
      /* The legacy g-header clones the entire .js-g-search panel into its
         hidden mobile menu (`.js-mobile-menu-inner`). That clone comes
         FIRST in DOM order, so a plain `.js-g-search .c-search-form__input`
         selector lands on the invisible (zero-size) cloned input. Pick the
         REAL panel — the one not nested inside the legacy mobile menu. */
      var panels = document.querySelectorAll('.js-g-search');
      for (var i = 0; i < panels.length; i++) {
        if (!panels[i].closest('.js-mobile-menu')) {
          return panels[i].querySelector('.c-search-form__input');
        }
      }
      return null;
    }

    /* The theme's `ariaFocusManager` watches for `aria-hidden` and proactively
       sets tabindex="-1" on every focusable child of any [aria-hidden="true"]
       element. Because the search panel ships with aria-hidden="true", the
       input is born unfocusable. When the panel opens we get the tabindex
       reset back via a MutationObserver — but that's async, so any
       synchronous .focus() we make right after opening lands on an element
       that's still tabindex="-1" and the call no-ops on some browsers.
       Force-reset it ourselves before calling focus(). */
    function focusSearchInput() {
      var input = getSearchInput();
      if (!input) return;
      if (input.getAttribute('tabindex') === '-1') input.removeAttribute('tabindex');
      input.focus({ preventScroll: true });
    }

    /* Mobile search field inside the nav drawer */
    if (mobileSearchTrigger && headerSearchToggle) {
      mobileSearchTrigger.addEventListener('click', function (e) {
        e.preventDefault();
        headerSearchToggle.click();
      });
    }

    /* Auto-focus the search input every time the search drawer opens.
       gSearch.searchOpen() only auto-focuses on >=601px, so phone users
       (including Chrome DevTools mobile emulation) never get a cursor in
       the field. Rather than racing the open handler, we observe the
       `is-search-active` class on <html> — the moment it appears, we
       focus the input. We also try a couple of follow-ups on the next
       frames because the theme's `ariaFocusManager` may stomp on tabindex
       in a microtask. */
    var rootEl = document.documentElement;
    var lastSearchActive = rootEl.classList.contains('is-search-active');
    var rootObserver = new MutationObserver(function () {
      var nowActive = rootEl.classList.contains('is-search-active');
      if (nowActive && !lastSearchActive) {
        focusSearchInput();
        requestAnimationFrame(focusSearchInput);
      }
      lastSearchActive = nowActive;
    });
    rootObserver.observe(rootEl, { attributes: true, attributeFilter: ['class'] });

    /* The theme's mobile search "Close" button (.js-search-close-mobile, rendered
       inside c-search-form) only toggles an internal class without actually
       closing the search drawer. Forward its click to the global search toggle
       so it dismisses the search the same way the overlay/Esc do. */
    if (headerSearchToggle) {
      document.addEventListener('click', function (e) {
        var closeBtn = e.target.closest && e.target.closest('.js-search-close-mobile');
        if (!closeBtn) return;
        if (document.documentElement.classList.contains('is-search-active')) {
          headerSearchToggle.click();
        }
      });
    }

    /* ---------- Desktop Mega Menu Dropdowns ---------- */
    var dropdownItems = header.querySelectorAll('.oddit-header__nav-item--has-dropdown');
    var dropdownButtons = header.querySelectorAll('.oddit-header__nav-link--dropdown');
    var megaCloseTimer = null;

    function cancelMegaCloseTimer() {
      if (megaCloseTimer) {
        clearTimeout(megaCloseTimer);
        megaCloseTimer = null;
      }
    }

    function scheduleMegaClose() {
      cancelMegaCloseTimer();
      megaCloseTimer = setTimeout(function () {
        megaCloseTimer = null;
        closeAllMegaMenus();
      }, 150);
    }

    function closeAllMegaMenus() {
      cancelMegaCloseTimer();
      dropdownItems.forEach(function (li) {
        var btn = li.querySelector('.oddit-header__nav-link--dropdown');
        var megaMenu = li.querySelector('.oddit-header__mega-menu');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (megaMenu) megaMenu.setAttribute('aria-hidden', 'true');
      });
    }

    function openMegaMenuFor(li) {
      var btn = li.querySelector('.oddit-header__nav-link--dropdown');
      var megaMenu = li.querySelector('.oddit-header__mega-menu');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      if (megaMenu) megaMenu.setAttribute('aria-hidden', 'false');
    }

    /* Hover on fine pointers; click-to-toggle on touch / no-hover devices */
    var prefersHover = window.matchMedia('(hover: hover)').matches;

    if (prefersHover) {
      dropdownItems.forEach(function (li) {
        li.addEventListener('mouseenter', function () {
          cancelMegaCloseTimer();
          closeAllMegaMenus();
          openMegaMenuFor(li);
        });
        li.addEventListener('mouseleave', function () {
          scheduleMegaClose();
        });
        li.addEventListener('focusin', function () {
          cancelMegaCloseTimer();
          closeAllMegaMenus();
          openMegaMenuFor(li);
        });
        li.addEventListener('focusout', function (e) {
          if (!li.contains(e.relatedTarget)) {
            closeAllMegaMenus();
          }
        });
      });
    } else {
      dropdownButtons.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          var li = btn.closest('.oddit-header__nav-item--has-dropdown');
          var isExpanded = btn.getAttribute('aria-expanded') === 'true';
          closeAllMegaMenus();
          if (!isExpanded && li) {
            openMegaMenuFor(li);
          }
        });
      });
    }

    /* Close mega menus when clicking outside */
    document.addEventListener('click', function (e) {
      if (!header.contains(e.target)) {
        closeAllMegaMenus();
      }
    });

    /* Close mega menus on Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeAllMegaMenus();
        closeMobileMenu();
      }
    });

    /* Close mega menus when mouse leaves the header area */
    var nav = header.querySelector('.oddit-header__nav');
    if (nav) {
      header.addEventListener('mouseleave', function () {
        closeAllMegaMenus();
      });
    }

    /* ---------- Search Header Spacing ----------
       Search button clicks are handled by gSearch (in js-main.js); its body
       click delegation already binds to .js-search-toggle. We only update
       --s-header-space here, and only when the oddit header is actually the
       visible header (otherwise we'd clobber the value gHeader sets for the
       legacy .g-header).
    */
    document.addEventListener('DOMContentLoaded', function () {
      function updateHeaderSpace() {
        var rect = header.getBoundingClientRect();
        if (rect.height === 0) return;
        var headerSpace = rect.top + rect.height;
        document.documentElement.style.setProperty('--s-header-space', headerSpace + 'px');
      }
      updateHeaderSpace();
      window.addEventListener('scroll', updateHeaderSpace);
      window.addEventListener('resize', updateHeaderSpace);
    });
  }

  /* ---------- Initialize ---------- */
  initHeader();

  /* ---------- Theme Editor Compatibility ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (section && section.querySelector('.oddit-header')) {
      initHeader(section.querySelector('.oddit-header'));
    }
  });
})();
